import React, { useRef, useState } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import AdminOrderQueue from '../components/LiveStamping/AdminOrderQueue';
import AdminMetadataReview from '../components/LiveStamping/AdminMetadataReview';
import AdminFinalReview from '../components/LiveStamping/AdminFinalReview';
import AdminWebhookConfig from '../components/LiveStamping/AdminWebhookConfig';
import AdminWebhookDeliveryLog from '../components/LiveStamping/AdminWebhookDeliveryLog';
import VendorSampleUpload from '../components/LiveStamping/VendorSampleUpload';
import VendorFinalUpload from '../components/LiveStamping/VendorFinalUpload';

const TABS = ['Pending Orders', 'Webhook Toggle', 'Webhook Log'];

const LiveStampingAdminConsole = () => {
  const { user } = useAuth();
  const queueRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);
  const [webhookTenantId, setWebhookTenantId] = useState('');
  const [webhookTenantInput, setWebhookTenantInput] = useState('');

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Access denied. Admin account required.</p>
      </div>
    );
  }

  const handleSelectOrder = (order) => setActiveOrder(order);

  const handleReviewSuccess = () => {
    setActiveOrder(null);
    queueRef.current?.refresh();
  };

  const getReviewModal = () => {
    if (!activeOrder) return null;

    if (activeOrder.status === 'executing') {
      return (
        <VendorSampleUpload
          order={activeOrder}
          onSuccess={handleReviewSuccess}
          onClose={() => setActiveOrder(null)}
        />
      );
    }
    if (activeOrder.status === 'preview_submitted') {
      return (
        <AdminMetadataReview
          order={activeOrder}
          onSuccess={handleReviewSuccess}
          onClose={() => setActiveOrder(null)}
        />
      );
    }
    if (activeOrder.status === 'metadata_ready') {
      return (
        <VendorFinalUpload
          order={activeOrder}
          onSuccess={handleReviewSuccess}
          onClose={() => setActiveOrder(null)}
        />
      );
    }
    return (
      <AdminFinalReview
        order={activeOrder}
        onSuccess={handleReviewSuccess}
        onClose={() => setActiveOrder(null)}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-indigo-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">Live Stamping — Admin Console</h1>
            <p className="text-xs text-gray-500">Logged in as {user.name}</p>
          </div>
        </div>
        <a href="/" className="text-sm text-gray-500 hover:text-gray-700 font-medium">← Back to Dashboard</a>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-0">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === i
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {activeTab === 0 && (
          <AdminOrderQueue ref={queueRef} onSelectOrder={handleSelectOrder} />
        )}

        {activeTab === 1 && (
          <div className="max-w-lg space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tenant ID</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={webhookTenantInput}
                  onChange={e => setWebhookTenantInput(e.target.value)}
                  placeholder="Enter tenant ID…"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  onKeyDown={e => { if (e.key === 'Enter') setWebhookTenantId(webhookTenantInput.trim()); }}
                />
                <button
                  onClick={() => setWebhookTenantId(webhookTenantInput.trim())}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  Load
                </button>
              </div>
            </div>
            <AdminWebhookConfig tenantId={webhookTenantId} />
          </div>
        )}

        {activeTab === 2 && (
          <AdminWebhookDeliveryLog />
        )}
      </div>

      {/* Review modals */}
      {getReviewModal()}
    </div>
  );
};

export default LiveStampingAdminConsole;
